import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SignupWithSubscription from './SignupWithSubscription';
import { StripeSessionStorage } from '../utils/secureStorage';

const SignupReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [verifyingPayment, setVerifyingPayment] = useState(true);
  const [paymentValid, setPaymentValid] = useState(false);

  useEffect(() => {
    // Redirect authenticated users to dashboard
    if (isAuthenticated && user) {
      navigate('/', { replace: true });
      return;
    }

    const sessionId = searchParams.get('session_id');
    const tier = searchParams.get('tier');

    // SECURITY: Validate session ID format
    if (sessionId && !sessionId.startsWith('cs_')) {
      console.error('Invalid session ID format detected:', sessionId);
      navigate('/signup', { replace: true });
      return;
    }

    // SECURITY: Validate tier
    const validTiers = ['elite'];
    if (tier && !validTiers.includes(tier)) {
      console.error('Invalid tier detected:', tier);
      navigate('/signup', { replace: true });
      return;
    }

    if (sessionId && tier) {
      // Verify the Stripe session
      verifyStripeSession(sessionId, tier);
    } else {
      // No session ID, redirect back to signup
      navigate('/signup', { replace: true });
    }
  }, [searchParams, navigate, isAuthenticated, user]);

  const verifyStripeSession = async (sessionId: string, tier: string) => {
    try {
      // Try multiple endpoints for session verification
      const endpoints = [
        '/api/verify-checkout-session', // Local API endpoint
        'https://eightythreelabs-verify-checkout-session.yahya-darman-work.workers.dev/', // Cloudflare Worker
      ];

      let verificationResult = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`Trying verification endpoint: ${endpoint}`);
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              session_id: sessionId,
              expected_tier: tier
            }),
          });

          if (response.ok) {
            verificationResult = await response.json();
            console.log(`Verification successful via ${endpoint}`);
            break;
          } else {
            lastError = new Error(`${endpoint} responded with status ${response.status}`);
            console.warn(lastError.message);
          }
        } catch (endpointError) {
          lastError = endpointError instanceof Error ? endpointError : new Error(String(endpointError));
          const errorMessage = endpointError instanceof Error ? endpointError.message : String(endpointError);
          console.warn(`Failed to reach ${endpoint}:`, errorMessage);
        }
      }

      if (!verificationResult) {
        throw lastError || new Error('All verification endpoints failed');
      }
      
      if (verificationResult.valid && verificationResult.payment_status === 'paid') {
        setPaymentValid(true);
        setVerifyingPayment(false);
        
        // Store VERIFIED payment info for account creation using secure storage
        StripeSessionStorage.setStripeSession(sessionId);
        StripeSessionStorage.setSubscriptionTier(verificationResult.tier);
        
        // Track TikTok conversion for successful payment
        if (typeof window !== 'undefined' && (window as any).ttq) {
          (window as any).ttq.track('Subscribe', {
            content_type: 'product',
            content_id: verificationResult.tier,
            value: verificationResult.amount_total / 100, // Convert from cents
            currency: verificationResult.currency?.toUpperCase()
          });
        }
      } else {
        throw new Error('Payment verification failed - session invalid or not paid');
      }
      
    } catch (error) {
      console.error('Payment verification failed:', error);
      
      // TEMPORARY FALLBACK: If verification endpoints are unavailable but we have valid session format,
      // proceed with account creation (this should be replaced with proper verification in production)
      if (sessionId.startsWith('cs_') && ['elite'].includes(tier)) {
        console.warn('⚠️ FALLBACK: Proceeding without backend verification (development mode)');
        
        // Store session info for account creation
        StripeSessionStorage.setStripeSession(sessionId);
        StripeSessionStorage.setSubscriptionTier(tier);
        
        // Track attempted conversion
        if (typeof window !== 'undefined' && (window as any).ttq) {
          (window as any).ttq.track('Subscribe', {
            content_type: 'product',
            content_id: tier,
            value: tier === 'elite' ? 99.99 : 29.99,
            currency: 'USD'
          });
        }
        
        setPaymentValid(true);
        setVerifyingPayment(false);
        return;
      }
      
      // If even the fallback conditions aren't met, redirect to signup
      setVerifyingPayment(false);
      setPaymentValid(false);
      navigate('/signup', { replace: true });
    }
  };

  if (verifyingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900/50 rounded-full mb-6">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Verifying Payment...
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Please wait while we confirm your subscription payment.
          </p>
        </div>
      </div>
    );
  }

  if (!paymentValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-black dark:to-gray-900 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/50 rounded-full mb-6">
            <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Verification Failed
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We couldn't verify your payment. Please try again or contact support.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Return to Signup
          </button>
        </div>
      </div>
    );
  }

  // Payment verified - show account creation step
  return <SignupWithSubscription initialStep="account" />;
};

export default SignupReturn;